import PropTypes from 'prop-types';
import { memo, useState, useEffect } from 'react';
import { Text, useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import BillboardBall from '../Ball';
import Groups from './Groups';
import { getBillboardPercentage, getBillboardPercentageLabel } from '../utils';

const propTypes = {
    model: PropTypes.object.isRequired,
    titleLiteral: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['sent', 'opened', 'confirmed']).isRequired,
    hasTabs: PropTypes.bool,
    open: PropTypes.bool,
    transitionEnd: PropTypes.func,
};

const TABS_LITERALS = {
    opened: ['label_billboard_status_users_open', 'label_billboard_status_users_not_open'],
    confirmed: [
        'label_billboard_status_users_confirmed',
        'label_billboard_status_users_not_confirmed',
    ],
};

const EMPTY_LITERALS = {
    sent: ['label_billboard_status_users_nobody_open'],
    opened: [
        'label_billboard_status_users_nobody_open',
        'label_billboard_status_users_nobody_open',
    ],
    confirmed: [
        'label_billboard_status_users_nobody_confirmed',
        'label_billboard_status_users_nobody_confirmed',
    ],
};

const GROUP_DATA = {
    sent: (model) => ({
        groups: model.actionDetailSentUsers,
        groupsCount: model.actionDetailSentUsersCount,
    }),
    opened: (model) => ({
        groups: model.actionDetailOpenedUsers,
        groupsCount: model.actionDetailOpenedUsersCount,
        nonGroups: model.actionDetailNonOpenedUsers,
        nonGroupsCount: model.actionDetailNonOpenedUsersCount,
    }),
    confirmed: (model) => ({
        groups: model.actionDetailConfirmedUsers,
        groupsCount: model.actionDetailConfirmedUsersCount,
        nonGroups: model.actionDetailNonConfirmedUsers,
        nonGroupsCount: model.actionDetailNonConfirmedUsersCount,
    }),
};

const BallContent = ({ type, model, theme }) => {
    if (type === 'sent') {
        return (
            <div className="ball-text-container-single">
                <Text type="h5" bold as="div" color={theme.colors.utility.textPrimary}>
                    {model.sent}
                </Text>
            </div>
        );
    }

    const value = type === 'opened' ? model.opened : model.confirmed;
    const percentageLabel = getBillboardPercentageLabel(model, type);

    return (
        <div className="ball-text-container">
            <Text type="h5" bold as="div" color={theme.colors.utility.textPrimary}>
                {value}
            </Text>
            <div className="ball-text-separator" />
            <Text type="caption" as="div" color={theme.colors.utility.textSecondary}>
                {percentageLabel}
            </Text>
        </div>
    );
};

const SecondaryPanel = memo(({ model, titleLiteral, hasTabs, open, transitionEnd, type }) => {
    const theme = useTheme();
    const [isMounted, setIsMounted] = useState(false);
    const percentage = type === 'sent' ? 100 : getBillboardPercentage(model, type);

    useEffect(() => {
        const timer = setTimeout(() => setIsMounted(true), 10);
        return () => clearTimeout(timer);
    }, []);

    const groupData = GROUP_DATA[type]?.(model) ?? {};

    return (
        <div
            className={['panel-secondary', open && isMounted ? 'show' : null]
                .filter(Boolean)
                .join(' ')}
            onTransitionEnd={transitionEnd.bind(null, type)}
        >
            <div className="panel-secondary-header">
                <Text
                    type="h5"
                    bold
                    as="div"
                    className="secondary-title"
                    color={theme.colors.utility.textPrimary}
                >
                    {getLiteral(titleLiteral)}
                </Text>
                <BillboardBall className="ball" percentage={percentage} strokeWidth={6}>
                    <BallContent type={type} model={model} theme={theme} />
                </BillboardBall>
            </div>
            <div className="panel-secondary-content">
                <Groups
                    hasTabs={hasTabs}
                    tabsLiterals={TABS_LITERALS[type]}
                    emptyLiterals={EMPTY_LITERALS[type]}
                    {...groupData}
                />
            </div>
        </div>
    );
});

SecondaryPanel.propTypes = propTypes;

export default SecondaryPanel;
